import { CleanUpTask } from '../types'
import { getRandomId } from './urPlayerUtils'

export type Option = {
  active: boolean
  label: string
  onClick: () => void
}

export type AddButton = (
  onClick: () => void,
  buttonAttr: string,
  className: string,
) => void

const buildMenu = (options: Option[]): [HTMLDivElement, CleanUpTask[]] => {
  const div = document.createElement('div')
  div.classList.add('video-quality-menu')
  const ul = document.createElement('ul')

  const cleanUpTasks: CleanUpTask[] = []

  options.forEach(option => {
    const li = document.createElement('li')
    li.innerHTML = option.label
    if (option.active) {
      li.classList.add('active')
    }
    ul.appendChild(li)

    const setActive = () => {
      Array.from(ul.children).forEach(child => child.classList.remove('active'))
      li.classList.add('active')
    }

    li.addEventListener('click', option.onClick)
    li.addEventListener('click', setActive)
    cleanUpTasks.push(() => li.removeEventListener('click', option.onClick))
    cleanUpTasks.push(() => li.removeEventListener('click', setActive))
  })

  div.appendChild(ul)

  return [div, cleanUpTasks]
}

export const addDisplayControls = (
  addButton: AddButton,
  className: string,
  callback?: () => void,
): CleanUpTask[] => {
  const buttonAttr = getRandomId()

  addButton(() => {}, buttonAttr, className)

  const buttonContainer = document.createElement('div')
  const button = document.querySelector(`div[button="${buttonAttr}"]`)

  buttonContainer.classList.add(
    `${className}-container`,
    'jw-display-icon-container',
    'jw-display-icon-display',
  )
  if (button) buttonContainer.appendChild(button)

  const parent = document.getElementsByClassName('jw-display-controls')[0]

  parent.appendChild(buttonContainer)

  if (callback !== undefined && button) {
    button.addEventListener('click', callback)
  }

  const cleanUpTasks: CleanUpTask[] = [
    () => {
      if (callback !== undefined && button) {
        button.removeEventListener('click', callback)
      }
    },
  ]

  return cleanUpTasks
}

export const addOptionControls = (
  options: Option[],
  addButton: AddButton,
  className: string,
  tooltipText: string,
  callback?: () => void,
  buttonIndex?: number,
): CleanUpTask[] => {
  const buttonAttr = getRandomId()
  addButton(() => {}, buttonAttr, `${className}-container`)

  const buttonContainer = document.querySelector(`div[button="${buttonAttr}"]`)
  if (buttonContainer) buttonContainer.setAttribute('aria-label', tooltipText)

  const button = document.createElement('div')
  button.classList.add(className)

  const [menu, cleanUpTasks] = buildMenu(options)
  if (options.length > 0 && buttonContainer) buttonContainer.appendChild(menu)

  const tooltipContainer = document.createElement('div')
  tooltipContainer.classList.add('jw-reset-text')
  tooltipContainer.classList.add('jw-tooltip')
  tooltipContainer.classList.add(`jw-tooltip-${className}`)

  const tooltip = document.createElement('div')
  tooltip.classList.add(`jw-text`)
  tooltip.innerText = tooltipText

  button.addEventListener('mouseenter', () => {
    tooltipContainer.classList.add('jw-open')
  })

  button.addEventListener('mouseleave', () => {
    tooltipContainer.classList.remove('jw-open')
  })

  const toggleMenuVisibility = () => {
    if (menu.style.display === 'block') {
      menu.style.display = 'none'
      tooltipContainer.classList.add('jw-open')
    } else {
      menu.style.display = 'block'
      tooltipContainer.classList.remove('jw-open')
    }
  }

  const hideMenuOnClickOutside = (event: Event) => {
    if (event.target !== menu && event.target !== button) {
      menu.style.display = 'none'
    }
  }

  tooltipContainer.appendChild(tooltip)
  if (buttonContainer) {
    buttonContainer.appendChild(tooltipContainer)
    buttonContainer.appendChild(button)
  }

  const leftSideButton = buttonIndex !== undefined && buttonIndex > -1

  if (leftSideButton) {
    const parent = document.getElementsByClassName('jw-button-container')[0]
    if (buttonContainer)
      parent.insertBefore(buttonContainer, parent.children[buttonIndex])
  }

  if (callback !== undefined) {
    button.addEventListener('click', callback)
  }

  if (options.length > 0) {
    button.addEventListener('click', toggleMenuVisibility)
    document.addEventListener('click', hideMenuOnClickOutside)
  }

  return cleanUpTasks.concat(() => {
    if (options.length > 0) {
      button.removeEventListener('click', toggleMenuVisibility)
      document.removeEventListener('click', hideMenuOnClickOutside)
    }
    if (callback !== undefined) {
      button.removeEventListener('click', callback)
    }
  })
}

export default addOptionControls
